import React, { useState, useEffect, useCallback } from 'react';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import RecodeLabelCard from './components/RecodeLabelCard';
import LabelDetails from './components/LabelDetails';
import RecodeLabelForm from './components/RecodeLabelForm';
import { apiClient } from '../../api/apiClient';
import { 
  GET_RECORD_LABELS_URL,
  CREATE_RECORD_LABEL_URL,
  UPDATE_RECORD_LABEL_URL,
  INACTIVATE_RECORD_LABEL_URL,
  ACTIVATE_RECORD_LABEL_URL,
  DELETE_RECORD_LABEL_URL,
  EXPORT_RECORD_LABELS_URL
} from '../../api/recordLabel';
import { toast } from '../../components/shared/toast';
import en from '../../translations/en.json';
import EmptyState from '../../components/shared/EmptyState';
import Loader from '../../components/shared/Loader';
import './RecodeLabels.css';
import '../Ringtone/Ringtone.css';

function RecodeLabels({ 
  onSelectSong,
  selectedLabel: externalSelectedLabel,
  onSelectLabel,
  onBackFromLabel
}) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateLabels = canCreate('record_labels');
  const canEditLabels = canEdit('record_labels');
  const canDeleteLabels = canDelete('record_labels');

  const [labels, setLabels] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [internalSelectedLabel, setInternalSelectedLabel] = useState(null);
  const selectedLabel = externalSelectedLabel !== undefined ? externalSelectedLabel : internalSelectedLabel;

  const handleSelectLabel = (lbl) => {
    if (onSelectLabel) {
      onSelectLabel(lbl);
    } else {
      setInternalSelectedLabel(lbl);
    }
  };

  const handleBackLabel = () => {
    if (onBackFromLabel) {
      onBackFromLabel();
    } else {
      setInternalSelectedLabel(null);
    }
  };

  const [editingLabel, setEditingLabel] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Modals state — Inactivate
  const [showConfirmInactivateModal, setShowConfirmInactivateModal] = useState(false);
  const [showInactivateWarningModal, setShowInactivateWarningModal] = useState(false);
  const [pendingInactivateLabel, setPendingInactivateLabel] = useState(null);
  const [inactivateDependentSongs, setInactivateDependentSongs] = useState([]);

  // Modals state — Reactivate
  const [showConfirmActivateModal, setShowConfirmActivateModal] = useState(false);
  const [pendingActivateLabel, setPendingActivateLabel] = useState(null);

  // Modals state — Delete
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [pendingDeleteLabel, setPendingDeleteLabel] = useState(null);
  const [deleteDependentSongs, setDeleteDependentSongs] = useState([]);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  // Fetch record labels from backend
  const fetchLabels = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `${GET_RECORD_LABELS_URL}?page=${currentPage}&limit=20&search=${encodeURIComponent(searchQuery.trim())}`;
      const response = await apiClient(url);
      if (response) {
        setLabels(response.labels || response.data || []);
        setTotalCount(response.totalCount || response.totalRecords || 0);
      }
    } catch (err) {
      console.error('Failed to fetch record labels:', err);
      setLabels([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery]);

  useEffect(() => {
    fetchLabels();
  }, [currentPage, searchQuery]);

  const handleSearchChange = (q) => {
    setSearchQuery(q);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const response = await apiClient(EXPORT_RECORD_LABELS_URL);
      if (response && (response.labels || response.data)) {
        const list = response.labels || response.data;
        const headers = ['Record Label Name', 'Status', 'Song Count', 'Created At'];
        const rows = list.map(l => [
          l.name,
          l.status,
          l.songCount || 0,
          l.created_at ? String(l.created_at).split('T')[0] : '—'
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `record_labels_export_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Record labels exported successfully!');
      } else {
        toast.error('Failed to export record labels.');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export record labels.');
    } finally {
      setIsExporting(false);
    }
  };

  // ─── INACTIVATE FLOW ───
  const handleInactivateClick = (label) => {
    setPendingInactivateLabel(label);
    setShowConfirmInactivateModal(true);
  };

  const handleConfirmInactivate = async () => {
    if (!pendingInactivateLabel) return;
    setShowConfirmInactivateModal(false);

    try {
      const response = await apiClient(INACTIVATE_RECORD_LABEL_URL(pendingInactivateLabel.id), {
        method: 'PUT'
      });

      if (response && response.hasDependencies) {
        setInactivateDependentSongs(response.dependentSongs || []);
        setShowInactivateWarningModal(true);
      } else if (response && response.success !== false) {
        toast.success('Record label deactivated successfully');
        setPendingInactivateLabel(null);
        fetchLabels();
        if (selectedLabel && selectedLabel.id === pendingInactivateLabel.id) {
          setSelectedLabel(prev => prev ? { ...prev, status: 'Inactive', statusCode: 0 } : null);
        }
      }
    } catch (err) {
      console.error('Error inactivating label:', err);
      toast.error(err.message || 'Failed to deactivate record label.');
    }
  };

  const handleForceInactivate = async () => {
    if (!pendingInactivateLabel) return;
    setShowInactivateWarningModal(false);

    try {
      const response = await apiClient(INACTIVATE_RECORD_LABEL_URL(pendingInactivateLabel.id), {
        method: 'PUT',
        body: { force: true }
      });

      if (response && response.success !== false) {
        toast.success('Record label deactivated successfully');
        setPendingInactivateLabel(null);
        setInactivateDependentSongs([]);
        fetchLabels();
        if (selectedLabel && selectedLabel.id === pendingInactivateLabel.id) {
          setSelectedLabel(prev => prev ? { ...prev, status: 'Inactive', statusCode: 0 } : null);
        }
      }
    } catch (err) {
      console.error('Error force inactivating label:', err);
      toast.error(err.message || 'Failed to deactivate record label.');
    }
  };

  // ─── REACTIVATE FLOW ───
  const handleActivateClick = (label) => {
    setPendingActivateLabel(label);
    setShowConfirmActivateModal(true);
  };

  const handleConfirmActivate = async () => {
    if (!pendingActivateLabel) return;
    setShowConfirmActivateModal(false);

    try {
      const response = await apiClient(ACTIVATE_RECORD_LABEL_URL(pendingActivateLabel.id), {
        method: 'PUT'
      });

      if (response && response.success !== false) {
        toast.success('Record label reactivated successfully');
        setPendingActivateLabel(null);
        fetchLabels();
        if (selectedLabel && selectedLabel.id === pendingActivateLabel.id) {
          setSelectedLabel(prev => prev ? { ...prev, status: 'Active', statusCode: 1 } : null);
        }
      }
    } catch (err) {
      console.error('Error activating label:', err);
      toast.error(err.message || 'Failed to reactivate record label.');
    }
  };

  // ─── DELETE FLOW ───
  const handleDeleteClick = (label) => {
    setPendingDeleteLabel(label);
    setShowConfirmDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteLabel) return;
    setShowConfirmDeleteModal(false);

    try {
      const response = await apiClient(DELETE_RECORD_LABEL_URL(pendingDeleteLabel.id), {
        method: 'DELETE'
      });

      if (response && response.hasDependencies) {
        setDeleteDependentSongs(response.dependentSongs || []);
        setShowDeleteWarningModal(true);
      } else if (response && response.success !== false) {
        toast.success('Record label deleted successfully');
        setPendingDeleteLabel(null);
        fetchLabels();
        if (selectedLabel && selectedLabel.id === pendingDeleteLabel.id) {
          setSelectedLabel(null);
        }
      }
    } catch (err) {
      console.error('Error deleting label:', err);
      toast.error(err.message || 'Failed to delete record label.');
    }
  };

  const handleForceDelete = async () => {
    if (!pendingDeleteLabel) return;
    setShowDeleteWarningModal(false);

    try {
      const response = await apiClient(DELETE_RECORD_LABEL_URL(pendingDeleteLabel.id), {
        method: 'DELETE',
        body: { force: true }
      });

      if (response && response.success !== false) {
        toast.success('Record label deleted successfully');
        setPendingDeleteLabel(null);
        setDeleteDependentSongs([]);
        fetchLabels();
        if (selectedLabel && selectedLabel.id === pendingDeleteLabel.id) {
          setSelectedLabel(null);
        }
      }
    } catch (err) {
      console.error('Error force deleting label:', err);
      toast.error(err.message || 'Failed to delete record label.');
    }
  };

  // ─── CREATE / EDIT FLOW ───
  const handleSaveForm = async (formData) => {
    try {
      if (editingLabel) {
        const response = await apiClient(UPDATE_RECORD_LABEL_URL(editingLabel.id), {
          method: 'PUT',
          body: formData
        });
        if (response) {
          toast.success('Record label updated successfully');
          setEditingLabel(null);
          fetchLabels();
        }
      } else {
        const response = await apiClient(CREATE_RECORD_LABEL_URL, {
          method: 'POST',
          body: formData
        });
        if (response) {
          toast.success('Record label created successfully');
          setIsAddingNew(false);
          fetchLabels();
        }
      }
    } catch (err) {
      console.error('Error saving record label:', err);
      toast.warning(err.message || 'Record label name already exists');
    }
  };

  if (editingLabel) {
    return (
      <RecodeLabelForm 
        existingLabel={editingLabel}
        onSave={handleSaveForm}
        onCancel={() => setEditingLabel(null)}
        t={t}
      />
    );
  }

  if (isAddingNew) {
    return (
      <RecodeLabelForm 
        onSave={handleSaveForm}
        onCancel={() => setIsAddingNew(false)}
        t={t}
      />
    );
  }

  const totalPages = Math.ceil(totalCount / 20) || 1;

  return (
    <ProtectedPage module="record_labels">
      <div className="recode-labels-page-wrapper">
      {selectedLabel ? (
        <LabelDetails 
          labelItem={selectedLabel} 
          onBack={handleBackLabel}
          onInactivate={handleInactivateClick}
          onActivate={handleActivateClick}
          onDelete={handleDeleteClick}
          onUpdateSuccess={fetchLabels}
          onSelectSong={onSelectSong}
          t={t}
        />
      ) : (
        <>
          {/* Shared Header Controls */}
          <SharedHeader
            title={t('recodeLabels.title') || 'Record Labels'}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            searchPlaceholder={t('recodeLabels.searchPlaceholder') || 'Search...'}
            resultCount={totalCount}
            resultLabel={t('recodeLabels.resultsCount') || 'Results'}
            onSortClick={null}
            onFilterClick={null}
            onExportClick={handleExport}
            onAddNewClick={canCreateLabels ? () => setIsAddingNew(true) : null}
            addNewText={t('common.addNew') || 'Add New'}
            canAddNew={canCreateLabels}
          />

          {/* Grid of Labels */}
          <div className="ringtone-grid-container">
            {isLoading ? (
              <LoadingOverlay isLoading message="Fetching record labels, please wait." />
            ) : labels.length > 0 ? (
              labels.map((lbl) => (
                <RecodeLabelCard 
                  key={lbl.id}
                  label={lbl}
                  onSelect={handleSelectLabel}
                  onEdit={canEditLabels ? setEditingLabel : null}
                  onInactivate={canEditLabels ? handleInactivateClick : null}
                  onActivate={canEditLabels ? handleActivateClick : null}
                  onDelete={canDeleteLabels ? handleDeleteClick : null}
                  t={t}
                />
              ))
            ) : (
              <EmptyState title="No Record Labels Found" subtitle="No record labels match your search criteria." hasSearch={true} />
            )}
          </div>

          {/* Pagination Controls Footer */}
          {totalCount > 0 && (
            <div className="ringtone-pagination-row">
              <span className="pagination-range-text">
                {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalCount)} of {totalCount}
              </span>
              <div className="pagination-controls-wrapper">
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num}
                    type="button" 
                    className={`pager-btn ${currentPage === num ? 'active' : 'outlined'}`}
                    onClick={() => setCurrentPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Primary Modal — Inactivate Confirmation */}
      <ConfirmationModal
        isOpen={showConfirmInactivateModal}
        title="Inactivate Record Label"
        message={`Are you sure you want to deactivate record label "${pendingInactivateLabel?.name}"?`}
        confirmLabel="Inactivate"
        cancelLabel="Cancel"
        onConfirm={handleConfirmInactivate}
        onCancel={() => setShowConfirmInactivateModal(false)}
      />

      {/* Secondary Modal — Inactivate Active Songs Warning */}
      {showInactivateWarningModal && (
        <div className="modal-backdrop-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="dependency-warning-popup" style={{ background: '#ffffff', borderRadius: '16px', padding: '28px', maxWidth: '540px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#d97706', marginBottom: '16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Active Songs Warning</h3>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
              This record label has active songs. Do you want to deactivate this label and remove these song relationships?
            </p>
            <div className="dependent-songs-list" style={{ maxHeight: '180px', overflowY: 'auto', background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
              {inactivateDependentSongs.map(song => (
                <div key={song.id} style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', padding: '4px 0' }}>
                  • {song.name} ({song.artist})
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                type="button" 
                onClick={() => setShowInactivateWarningModal(false)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleForceInactivate}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#d97706', color: '#ffffff', fontWeight: '600', cursor: 'pointer' }}
              >
                Deactivate Label & Remove Links
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Primary Modal — Activate Confirmation */}
      <ConfirmationModal
        isOpen={showConfirmActivateModal}
        title="Reactivate Record Label"
        message={`Are you sure you want to reactivate record label "${pendingActivateLabel?.name}"?`}
        confirmLabel="Reactivate"
        cancelLabel="Cancel"
        onConfirm={handleConfirmActivate}
        onCancel={() => setShowConfirmActivateModal(false)}
      />

      {/* Primary Modal — Delete Confirmation */}
      <ConfirmationModal
        isOpen={showConfirmDeleteModal}
        title="Delete Record Label"
        message={`Are you sure you want to soft delete record label "${pendingDeleteLabel?.name}"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirmDeleteModal(false)}
      />

      {/* Secondary Modal — Delete Warning */}
      {showDeleteWarningModal && (
        <div className="modal-backdrop-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div className="dependency-warning-popup" style={{ background: '#ffffff', borderRadius: '16px', padding: '28px', maxWidth: '540px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ef4444', marginBottom: '16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 1 1.71-3L13.71 3.86a2 2 0 0 1-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>Associated Songs Warning</h3>
            </div>
            <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
              This inactive record label still has associated songs. Are you sure you want to soft delete this label and remove these song relationships?
            </p>
            <div className="dependent-songs-list" style={{ maxHeight: '180px', overflowY: 'auto', background: '#f8fafc', borderRadius: '8px', padding: '12px', marginBottom: '20px', border: '1px solid #e2e8f0' }}>
              {deleteDependentSongs.map(song => (
                <div key={song.id} style={{ fontSize: '13px', fontWeight: '600', color: '#0f172a', padding: '4px 0' }}>
                  • {song.name} ({song.artist})
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button 
                type="button" 
                onClick={() => setShowDeleteWarningModal(false)}
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleForceDelete}
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#ffffff', fontWeight: '600', cursor: 'pointer' }}
              >
                Delete Label & Remove Links
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export loading overlay */}
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      </div>
    </ProtectedPage>
  );
}

export default RecodeLabels;
